import React, { useCallback, useEffect, useMemo, useState } from 'react';
import execucaoFinanceiraService, {
  rotuloContrato,
  type ExecucaoFinanceiraMovimento,
  type ExecucaoFinanceiraResumo,
  type TipoMovimentoFinanceiro,
} from '../services/execucaoFinanceiraService';
import { UI_THEME } from '../ui/theme';
import ContratosEAtosLegaisSection from './ContratosEAtosLegaisSection';
import { toUpperText } from '../utils/formatters';

type AcaoFormulario = TipoMovimentoFinanceiro | 'ANULACAO';

type Props = {
  emendaId: string;
  canRegister: Record<TipoMovimentoFinanceiro, boolean>;
  canAnnul: Record<TipoMovimentoFinanceiro, boolean>;
  refreshKey?: number;
};

const TIPO_LABEL: Record<TipoMovimentoFinanceiro, string> = {
  EMPENHO: 'Empenho',
  LIQUIDACAO: 'Liquidação',
  PAGAMENTO: 'Pagamento',
};

const SITUACAO_LABEL: Record<string, string> = {
  AGUARDANDO_EMPENHO: 'Aguardando empenho',
  EMPENHADA_PARCIALMENTE: 'Empenhada parcialmente',
  EMPENHADA: 'Empenhada',
  LIQUIDADA_PARCIALMENTE: 'Liquidada parcialmente',
  LIQUIDADA: 'Liquidada',
  PAGA_PARCIALMENTE: 'Paga parcialmente',
  PAGA: 'Paga',
  EXECUCAO_CANCELADA: 'Execução cancelada',
};

const hoje = () => new Date().toISOString().slice(0, 10);
const money = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatCurrencyInput = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return (Number(digits) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const parseCurrencyInput = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits ? Number(digits) / 100 : 0;
};
const date = (value?: string) => value ? new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR') : '—';
const numeroComAno = (numero: string, dataMovimento?: string) => {
  const ano = dataMovimento?.slice(0, 4);
  return ano ? `${numero}/${ano}` : numero;
};
const errorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: unknown } } }).response;
    if (typeof response?.data?.error === 'string') return response.data.error;
  }
  return fallback;
};

const ExecucaoFinanceiraPanel: React.FC<Props> = ({ emendaId, canRegister, canAnnul, refreshKey = 0 }) => {
  const isSaas = UI_THEME === 'saas';
  const [resumo, setResumo] = useState<ExecucaoFinanceiraResumo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acao, setAcao] = useState<AcaoFormulario | null>(null);
  const [movimentoAnulado, setMovimentoAnulado] = useState<ExecucaoFinanceiraMovimento | null>(null);
  const [form, setForm] = useState({
    movimentoOrigemId: '', numeroDocumento: '', dataMovimento: hoje(), valor: '',
    observacao: '', documentoReferencia: '', justificativa: '',
  });

  const panelClass = isSaas
    ? 'rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/60'
    : 'rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900';
  const primaryClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700'
    : 'bg-sky-700 text-white hover:bg-sky-800';
  const surfaceRadius = 'rounded-2xl';
  const controlRadius = 'rounded-xl';
  const fieldClass = `mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 outline-none transition dark:border-slate-600 dark:bg-slate-800 ${isSaas
    ? 'focus:border-teal-500 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/40'
    : 'focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:focus:ring-sky-900/40'}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResumo(await execucaoFinanceiraService.getResumo(emendaId));
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível carregar a execução financeira.'));
    } finally {
      setLoading(false);
    }
  }, [emendaId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const origensElegiveis = useMemo(() => {
    if (!resumo || (acao !== 'LIQUIDACAO' && acao !== 'PAGAMENTO')) return [];
    const tipoOrigem = acao === 'LIQUIDACAO' ? 'EMPENHO' : 'LIQUIDACAO';
    return resumo.movimentos.filter((item) =>
      item.natureza === 'ORIGINAL' && item.tipo === tipoOrigem && item.saldoDisponivel > 0,
    );
  }, [acao, resumo]);

  const abrirFormulario = (novaAcao: AcaoFormulario, movimento?: ExecucaoFinanceiraMovimento) => {
    const tipoOrigem = novaAcao === 'LIQUIDACAO' ? 'EMPENHO' : novaAcao === 'PAGAMENTO' ? 'LIQUIDACAO' : null;
    const primeiraOrigem = resumo?.movimentos.find((item) =>
      item.natureza === 'ORIGINAL' && item.tipo === tipoOrigem && item.saldoDisponivel > 0,
    );
    setAcao(novaAcao);
    setMovimentoAnulado(movimento ?? null);
    setError(null);
    setForm({
      movimentoOrigemId: primeiraOrigem?.id ?? '', numeroDocumento: '', dataMovimento: hoje(), valor: '',
      observacao: '', documentoReferencia: '', justificativa: '',
    });
  };

  const fecharFormulario = () => {
    setAcao(null);
    setMovimentoAnulado(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!acao) return;
    const valor = parseCurrencyInput(form.valor);
    if (!Number.isFinite(valor) || valor <= 0) {
      setError('Informe um valor maior que zero.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = acao === 'ANULACAO' && movimentoAnulado
        ? await execucaoFinanceiraService.anular(emendaId, movimentoAnulado.id, {
            numeroDocumento: form.numeroDocumento,
            dataMovimento: form.dataMovimento,
            valor,
            justificativa: form.justificativa,
            documentoReferencia: form.documentoReferencia || undefined,
          })
        : await execucaoFinanceiraService.registrar(emendaId, {
            tipo: acao as TipoMovimentoFinanceiro,
            movimentoOrigemId: form.movimentoOrigemId || undefined,
            numeroDocumento: form.numeroDocumento,
            dataMovimento: form.dataMovimento,
            valor,
            observacao: form.observacao || undefined,
            documentoReferencia: form.documentoReferencia || undefined,
          });
      setResumo(updated);
      fecharFormulario();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível salvar o movimento.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <section id="execucao-financeira" className={`${panelClass} p-6 text-sm text-slate-500`}>Carregando execução financeira...</section>;
  }

  if (!resumo) {
    return <section id="execucao-financeira" className={`${panelClass} p-6 text-sm text-red-600`}>{error || 'Execução financeira indisponível.'}</section>;
  }

  const temImportacaoOficial = resumo.movimentos.some((item) => item.origem === 'COPLAN');
  const contratoDoMovimento = (item: ExecucaoFinanceiraMovimento) =>
    item.contratoId ? resumo.contratos.find((contrato) => contrato.id === item.contratoId) : undefined;

  return (
    <section id="execucao-financeira" className={`${panelClass} scroll-mt-24 overflow-hidden`}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <div>
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Após o plano de trabalho</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">Execução financeira</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Empenhos, liquidações, pagamentos e respectivas anulações.</p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${resumo.liberada ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>
          {resumo.liberada ? (SITUACAO_LABEL[resumo.situacao] ?? resumo.situacao) : 'Aguardando conclusão do plano'}
        </span>
      </div>

      <div className="space-y-5 p-5">
        {!resumo.liberada && (
          <div className={`${surfaceRadius} border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200`}>
            {resumo.motivoBloqueio}
          </div>
        )}

        {resumo.liberada && !resumo.documentoLiberacaoAprovado && (
          <div className={`${surfaceRadius} border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200`}>
            {resumo.motivoBloqueioLiquidacao} O empenho permanece disponível.
          </div>
        )}

        {temImportacaoOficial && (
          <div className={`${surfaceRadius} border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300`}>
            Esta emenda já tem execução financeira oficial importada do Coplan — a Coplan é a fonte da verdade, então lançamento e anulação manuais ficam desabilitados aqui.
          </div>
        )}

        <div className={`grid overflow-hidden border border-slate-200 bg-white sm:grid-cols-4 dark:border-slate-700 dark:bg-slate-900/40 ${surfaceRadius}`}>
          {[
            ['Valor orçado', resumo.valorOrcado, null],
            ['Empenhado líquido', resumo.empenhadoLiquido, resumo.empenhoAnulado],
            ['Liquidado líquido', resumo.liquidadoLiquido, resumo.liquidacaoAnulada],
            ['Pago líquido', resumo.pagoLiquido, resumo.pagamentoEstornado],
          ].map(([label, value, annulled], index) => (
            <div key={String(label)} className={`px-4 py-3 ${index > 0 ? 'border-t border-slate-200 sm:border-l sm:border-t-0 dark:border-slate-700' : ''}`}>
              <p className="text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{String(label)}</p>
              <p className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">{money(Number(value))}</p>
              {annulled != null && Number(annulled) > 0 && <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">Anulado: {money(Number(annulled))}</p>}
            </div>
          ))}
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          {[
            ['Saldo a empenhar', resumo.saldoEmpenhar],
            ['Saldo a liquidar', resumo.saldoLiquidar],
            ['Saldo a pagar', resumo.saldoPagar],
          ].map(([label, value]) => (
            <div key={String(label)} className={`flex items-center justify-between bg-slate-50 px-4 py-2.5 text-sm dark:bg-slate-800 ${surfaceRadius}`}>
              <span className="text-slate-500 dark:text-slate-400">{String(label)}</span>
              <strong className="text-slate-800 dark:text-slate-100">{money(Number(value))}</strong>
            </div>
          ))}
        </div>

        {resumo.liberada && !temImportacaoOficial && Object.values(canRegister).some(Boolean) && !acao && (
          <div className="flex flex-wrap gap-2">
            {canRegister.EMPENHO && <button type="button" onClick={() => abrirFormulario('EMPENHO')} disabled={resumo.saldoEmpenhar <= 0} className={`${controlRadius} px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${primaryClass}`}>Registrar empenho</button>}
            {canRegister.LIQUIDACAO && <button type="button" onClick={() => abrirFormulario('LIQUIDACAO')} disabled={!resumo.documentoLiberacaoAprovado || !resumo.movimentos.some((m) => m.tipo === 'EMPENHO' && m.natureza === 'ORIGINAL' && m.saldoDisponivel > 0)} className={`${controlRadius} border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800`}>Registrar liquidação</button>}
            {canRegister.PAGAMENTO && <button type="button" onClick={() => abrirFormulario('PAGAMENTO')} disabled={!resumo.movimentos.some((m) => m.tipo === 'LIQUIDACAO' && m.natureza === 'ORIGINAL' && m.saldoDisponivel > 0)} className={`${controlRadius} border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800`}>Registrar pagamento</button>}
          </div>
        )}

        {acao && (
          <form onSubmit={submit} className={`space-y-4 border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40 ${surfaceRadius}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{acao === 'ANULACAO' ? `Anular ${movimentoAnulado ? TIPO_LABEL[movimentoAnulado.tipo].toLowerCase() : 'movimento'}` : `Registrar ${TIPO_LABEL[acao].toLowerCase()}`}</h3>
                {movimentoAnulado && <p className="mt-0.5 text-xs text-slate-500">Documento original: {numeroComAno(movimentoAnulado.numeroDocumento, movimentoAnulado.dataMovimento)} · saldo anulável {money(movimentoAnulado.saldoAnulavel)}</p>}
              </div>
              <button type="button" onClick={fecharFormulario} className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">Cancelar</button>
            </div>

            {(acao === 'LIQUIDACAO' || acao === 'PAGAMENTO') && (
              <label className="block text-sm text-slate-700 dark:text-slate-200">
                Movimento de origem
                <select required value={form.movimentoOrigemId} onChange={(e) => setForm((prev) => ({ ...prev, movimentoOrigemId: e.target.value }))} className={fieldClass}>
                  <option value="">Selecione</option>
                  {origensElegiveis.map((item) => <option key={item.id} value={item.id}>{numeroComAno(item.numeroDocumento, item.dataMovimento)} · disponível {money(item.saldoDisponivel)}</option>)}
                </select>
              </label>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-sm text-slate-700 dark:text-slate-200">Número do documento<input required value={form.numeroDocumento} onChange={(e) => setForm((prev) => ({ ...prev, numeroDocumento: toUpperText(e.target.value) }))} className={fieldClass} /></label>
              <label className="text-sm text-slate-700 dark:text-slate-200">Data<input type="date" required value={form.dataMovimento} onChange={(e) => setForm((prev) => ({ ...prev, dataMovimento: e.target.value }))} className={fieldClass} /></label>
              <label className="text-sm text-slate-700 dark:text-slate-200">Valor<input inputMode="numeric" required value={form.valor} onChange={(e) => setForm((prev) => ({ ...prev, valor: formatCurrencyInput(e.target.value) }))} placeholder="R$ 0,00" className={fieldClass} /></label>
            </div>

            {acao !== 'ANULACAO' && (
              <div className="block text-sm text-slate-700 dark:text-slate-200">
                Instituição favorecida
                <div className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200">
                  {resumo.instituicaoNome || 'Instituição não vinculada'}
                </div>
              </div>
            )}
            <label className="block text-sm text-slate-700 dark:text-slate-200">Referência do documento<input value={form.documentoReferencia} onChange={(e) => setForm((prev) => ({ ...prev, documentoReferencia: toUpperText(e.target.value) }))} placeholder="Processo, protocolo ou endereço do documento" className={fieldClass} /></label>
            <label className="block text-sm text-slate-700 dark:text-slate-200">{acao === 'ANULACAO' ? 'Justificativa' : 'Observação'}<textarea required={acao === 'ANULACAO'} rows={3} value={acao === 'ANULACAO' ? form.justificativa : form.observacao} onChange={(e) => setForm((prev) => ({ ...prev, [acao === 'ANULACAO' ? 'justificativa' : 'observacao']: toUpperText(e.target.value) }))} className={fieldClass} /></label>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            <div className="flex justify-end">
              <button type="submit" disabled={saving} className={`${controlRadius} px-4 py-2 text-sm font-semibold disabled:opacity-50 ${acao === 'ANULACAO' ? 'bg-red-600 text-white hover:bg-red-700' : primaryClass}`}>{saving ? 'Salvando...' : acao === 'ANULACAO' ? 'Confirmar anulação' : 'Salvar movimento'}</button>
            </div>
          </form>
        )}

        {!acao && error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <ContratosEAtosLegaisSection contratos={resumo.contratos} atosLegais={resumo.atosLegais} />

        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Histórico financeiro</h3>
          {resumo.movimentos.length === 0 ? (
            <p className={`mt-3 border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400 ${surfaceRadius}`}>Nenhum movimento financeiro registrado.</p>
          ) : (
            <div className={`mt-3 divide-y divide-slate-200 overflow-hidden border border-slate-200 dark:divide-slate-700 dark:border-slate-700 ${surfaceRadius}`}>
              {resumo.movimentos.map((item) => (
                <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 bg-white px-4 py-3 dark:bg-slate-900/40">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ${item.natureza === 'ANULACAO' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'}`}>{item.natureza === 'ANULACAO' ? `Anulação de ${TIPO_LABEL[item.tipo].toLowerCase()}` : TIPO_LABEL[item.tipo]}</span>
                      {item.origem === 'COPLAN' && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300" title="Importado automaticamente do sistema contábil oficial (Coplan)">
                          Importado do Coplan
                        </span>
                      )}
                      {item.substituidoPorCoplan && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.6875rem] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" title="Este lançamento manual foi substituído pelo registro oficial importado do Coplan e não entra mais na soma">
                          Substituído pelo Coplan
                        </span>
                      )}
                      {contratoDoMovimento(item) && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[0.6875rem] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" title="Contrato ao qual este empenho está vinculado no Coplan">
                          {rotuloContrato(contratoDoMovimento(item)!)}
                        </span>
                      )}
                      <span className={`text-sm font-semibold ${item.substituidoPorCoplan ? 'text-slate-400 line-through dark:text-slate-500' : 'text-slate-800 dark:text-slate-100'}`}>{numeroComAno(item.numeroDocumento, item.dataMovimento)}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{date(item.dataMovimento)} · {item.credor || item.justificativa || 'Sem favorecido informado'} · por {item.usuarioRegistro}</p>
                    {item.observacao && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{item.observacao}</p>}
                  </div>
                  <div className="flex items-center gap-3">
                    <strong className={item.natureza === 'ANULACAO' ? 'text-red-600 dark:text-red-400' : item.substituidoPorCoplan ? 'text-slate-400 line-through dark:text-slate-500' : 'text-slate-900 dark:text-slate-100'}>{item.natureza === 'ANULACAO' ? '− ' : ''}{money(item.valor)}</strong>
                    {canAnnul[item.tipo] && item.natureza === 'ORIGINAL' && item.saldoAnulavel > 0 && !temImportacaoOficial && (
                      <button type="button" onClick={() => abrirFormulario('ANULACAO', item)} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20">Anular</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ExecucaoFinanceiraPanel;
